
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import EnhancedProfileSection from '@/components/account/EnhancedProfileSection';
import AddressSection from '@/components/account/AddressSection';
import SecuritySection from '@/components/account/SecuritySection';
import KYCSection from '@/components/account/KYCSection';
import UserAvatar from '@/components/UserAvatar';
import EnhancedProfilePictureUpload from '@/components/account/EnhancedProfilePictureUpload';
import ProfileCompletionGuide from '@/components/account/ProfileCompletionGuide';
import { MessageSquare, User, MapPin, Shield, Upload, BarChart3 } from 'lucide-react';
import KYCDocumentUpload from '@/components/auth/KYCDocumentUpload';
import Header from '@/components/Header';
import { checkProfileCompletion } from '@/utils/profileValidation';

const Account = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [showKYCUpload, setShowKYCUpload] = useState(false);
  const [showCompletionGuide, setShowCompletionGuide] = useState(false);

  // Check if profile completion guide should be shown
  useEffect(() => {
    if (profile) {
      const completionResult = checkProfileCompletion(profile, 'contact');
      
      // Show guide if profile is incomplete and user hasn't seen it in this session
      if (!completionResult.isComplete) {
        const hasSeenGuide = sessionStorage.getItem('profile_completion_guide_seen');
        if (!hasSeenGuide) {
          setShowCompletionGuide(true);
          sessionStorage.setItem('profile_completion_guide_seen', 'true');
        }
      }
    }
  }, [profile]);

  // Check URL params for tab selection (e.g., ?tab=kyc)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'kyc') {
      // Scroll to KYC section or switch to KYC tab
      const kycTab = document.querySelector('[data-value="kyc"]') as HTMLElement;
      kycTab?.click();
    }
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('please_sign_in', 'يرجى تسجيل الدخول', 'Please sign in')}</h2>
          <Button onClick={() => window.location.href = '/signin'}>
            {t('sign_in', 'تسجيل الدخول', 'Sign In')}
          </Button>
        </div>
      </div>
    );
  }

  const getKYCStatusBadge = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return <Badge variant="default" className="bg-green-500">{t('verified', 'مُتحقق', 'Verified')}</Badge>;
      case 'PENDING':
        return <Badge variant="secondary">{t('pending', 'في الانتظار', 'Pending')}</Badge>;
      case 'UNDER_REVIEW':
        return <Badge variant="outline">{t('under_review', 'قيد المراجعة', 'Under Review')}</Badge>;
      case 'REJECTED':
        return <Badge variant="destructive">{t('rejected', 'مرفوض', 'Rejected')}</Badge>;
      default:
        return <Badge variant="secondary">{t('not_started', 'لم يبدأ', 'Not Started')}</Badge>;
    }
  };

  if (showKYCUpload) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <KYCDocumentUpload 
              onComplete={() => setShowKYCUpload(false)}
              userProfile={profile}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5">
      <Header />
      
      {/* Profile Completion Guide */}
      <ProfileCompletionGuide
        isOpen={showCompletionGuide}
        onClose={() => setShowCompletionGuide(false)}
      />
      
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Profile Header */}
          <Card>
            <CardHeader className="p-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <EnhancedProfilePictureUpload />
                <div className="space-y-2">
                  <CardTitle className="text-2xl font-semibold">{profile?.full_name || t('anonymous_user', 'مستخدم مجهول', 'Anonymous User')}</CardTitle>
                  <CardDescription className="text-base">{profile?.email}</CardDescription>
                  <div className="flex flex-wrap justify-center items-center gap-2 mt-3">
                    <Badge variant="outline" className="text-sm px-3 py-1">
                      {profile?.role === 'student' ? t('student', 'طالب', 'Student') : t('landlord', 'مالك', 'Landlord')}
                    </Badge>
                    {getKYCStatusBadge(profile?.kyc_status)}
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Main Content Tabs */}
          <div>
            <Tabs defaultValue="profile" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 gap-1 p-1 h-auto bg-muted/50 md:grid-cols-5">
                <TabsTrigger value="profile" className="flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md md:px-3 md:py-3 md:text-sm">
                  <User className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t('profile', 'الملف', 'Profile')}</span>
                </TabsTrigger>
                <TabsTrigger value="address" className="flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md md:px-3 md:py-3 md:text-sm">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t('address', 'العنوان', 'Address')}</span>
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md md:px-3 md:py-3 md:text-sm">
                  <Shield className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t('security', 'الأمان', 'Security')}</span>
                </TabsTrigger>
                <TabsTrigger value="kyc" className="flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md md:px-3 md:py-3 md:text-sm">
                  <Upload className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t('kyc_short', 'التحقق', 'KYC')}</span>
                </TabsTrigger>
                <TabsTrigger value="stats" className="col-span-2 flex items-center justify-center gap-2 px-2 py-2 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md md:col-span-1 md:px-3 md:py-3 md:text-sm">
                  <BarChart3 className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t('stats_short', 'إحصائيات', 'Stats')}</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="space-y-4">
                <EnhancedProfileSection />
              </TabsContent>

              <TabsContent value="address" className="space-y-4">
                <AddressSection />
              </TabsContent>

              <TabsContent value="security" className="space-y-4">
                <SecuritySection />
              </TabsContent>

              <TabsContent value="kyc" className="space-y-4">
                <KYCSection onUploadClick={() => setShowKYCUpload(true)} />
              </TabsContent>

              <TabsContent value="stats" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      {t('contact_statistics', 'إحصائيات التواصل', 'Contact Statistics')}
                    </CardTitle>
                    <CardDescription>
                      {t('whatsapp_contact_desc', 'عدد مرات التواصل مع المالكين عبر واتساب', 'Number of times you contacted property owners via WhatsApp')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center p-8">
                      <div className="text-4xl font-bold text-primary mb-2">
                        {profile?.whatsapp_contact_count || 0}
                      </div>
                      <p className="text-muted-foreground">
                        {t('whatsapp_contacts', 'تواصل عبر واتساب', 'WhatsApp Contacts')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
